import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { DD } from "@/constants/testIds";
import { Loader2, Search, RotateCcw } from "lucide-react";

// Curated list of ~50 countries where CIFF operates or is likely to screen.
// Countries with a specific regulator get a custom id_label; the rest fall
// back to a generic "Registration ID" prompt.
const COUNTRIES = [
    { code: "US", label: "United States", id_label: "EIN", placeholder: "13-1234567" },
    { code: "UK", label: "United Kingdom", id_label: "Charity Number", placeholder: "1234567" },
    { code: "IN", label: "India", id_label: "PAN / FCRA Number", placeholder: "AAATC0000A" },
    { code: "AU", label: "Australia", id_label: "ABN / ACNC", placeholder: "12 345 678 901" },
    { code: "CA", label: "Canada", id_label: "CRA Charity Number", placeholder: "123456789RR0001" },
    { code: "NZ", label: "New Zealand" },
    { code: "IE", label: "Ireland" },
    { code: "FR", label: "France" },
    { code: "DE", label: "Germany" },
    { code: "NL", label: "Netherlands" },
    { code: "BE", label: "Belgium" },
    { code: "CH", label: "Switzerland" },
    { code: "AT", label: "Austria" },
    { code: "SE", label: "Sweden" },
    { code: "NO", label: "Norway" },
    { code: "DK", label: "Denmark" },
    { code: "FI", label: "Finland" },
    { code: "IT", label: "Italy" },
    { code: "ES", label: "Spain" },
    { code: "PT", label: "Portugal" },
    { code: "PL", label: "Poland" },
    { code: "CZ", label: "Czech Republic" },
    { code: "EU", label: "European Union body" },
    { code: "BR", label: "Brazil" },
    { code: "MX", label: "Mexico" },
    { code: "AR", label: "Argentina" },
    { code: "CO", label: "Colombia" },
    { code: "CL", label: "Chile" },
    { code: "PE", label: "Peru" },
    { code: "ZA", label: "South Africa" },
    { code: "KE", label: "Kenya" },
    { code: "NG", label: "Nigeria" },
    { code: "GH", label: "Ghana" },
    { code: "ET", label: "Ethiopia" },
    { code: "UG", label: "Uganda" },
    { code: "TZ", label: "Tanzania" },
    { code: "RW", label: "Rwanda" },
    { code: "EG", label: "Egypt" },
    { code: "MA", label: "Morocco" },
    { code: "SN", label: "Senegal" },
    { code: "CN", label: "China" },
    { code: "JP", label: "Japan" },
    { code: "KR", label: "South Korea" },
    { code: "SG", label: "Singapore" },
    { code: "HK", label: "Hong Kong SAR" },
    { code: "TH", label: "Thailand" },
    { code: "VN", label: "Vietnam" },
    { code: "PH", label: "Philippines" },
    { code: "ID", label: "Indonesia" },
    { code: "MY", label: "Malaysia" },
    { code: "BD", label: "Bangladesh" },
    { code: "PK", label: "Pakistan" },
    { code: "LK", label: "Sri Lanka" },
    { code: "NP", label: "Nepal" },
    { code: "AE", label: "United Arab Emirates" },
    { code: "SA", label: "Saudi Arabia" },
    { code: "IL", label: "Israel" },
    { code: "TR", label: "Türkiye" },
    { code: "OTHER", label: "Other / Not listed" },
];

function metaFor(code) {
    return COUNTRIES.find((c) => c.code === code) || COUNTRIES[COUNTRIES.length - 1];
}

export default function LookupForm({ status, onStart, onReset }) {
    const [ngoName, setNgoName] = useState("");
    const [country, setCountry] = useState("US");
    const [regId, setRegId] = useState("");
    const [website, setWebsite] = useState("");
    const [mode, setMode] = useState("fast");

    const selected = metaFor(country);
    const idLabel = selected.id_label || "Registration ID";
    const idPlaceholder = selected.placeholder || "Registration / charity number";

    const running = status === "running";
    const canSubmit = ngoName.trim().length >= 2 && !running;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onStart({
            ngoName: ngoName.trim(),
            country,
            countryName: selected.label,
            regId: regId.trim(),
            website: website.trim(),
            mode,
        });
    };

    return (
        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-5">
            <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-ciff-inkSoft font-mono mb-1">
                    Subject organisation
                </div>
                <h2 className="font-heading text-2xl font-bold text-ciff-ink leading-tight tracking-tight">
                    Organisation Lookup
                </h2>
                <p className="text-xs text-ciff-inkSoft mt-1 leading-relaxed">
                    Enter the legal name (non-profit or for-profit) and, ideally,
                    a registration identifier and official website. Country
                    determines which regulator is queried first.
                </p>
            </div>

            <div className="space-y-1.5">
                <Label htmlFor={DD.ngoNameInput} className="text-[11px] font-semibold text-ciff-ink uppercase tracking-wider">
                    Organisation Legal Name
                </Label>
                <Input
                    id={DD.ngoNameInput}
                    data-testid={DD.ngoNameInput}
                    value={ngoName}
                    onChange={(e) => setNgoName(e.target.value)}
                    placeholder="e.g. Pratham Education Foundation, or Oxford Policy Management Pvt Ltd"
                    className="font-body text-sm rounded-sm border-border bg-white focus-visible:ring-ciff-ink/20"
                    disabled={running}
                    autoFocus
                />
            </div>

            <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold text-ciff-ink uppercase tracking-wider">
                    Country of Registration
                </Label>
                <Select value={country} onValueChange={setCountry} disabled={running}>
                    <SelectTrigger
                        data-testid={DD.countrySelectTrigger}
                        className="rounded-sm text-sm border-border bg-white"
                    >
                        <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                        {COUNTRIES.map((c) => (
                            <SelectItem
                                key={c.code}
                                value={c.code}
                                data-testid={DD.countryOption(c.code)}
                            >
                                {c.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-1.5">
                <Label htmlFor={DD.regIdInput} className="text-[11px] font-semibold text-ciff-ink uppercase tracking-wider">
                    {idLabel} <span className="text-ciff-inkSoft normal-case tracking-normal font-normal">(optional)</span>
                </Label>
                <Input
                    id={DD.regIdInput}
                    data-testid={DD.regIdInput}
                    value={regId}
                    onChange={(e) => setRegId(e.target.value)}
                    placeholder={idPlaceholder}
                    className="font-mono text-sm rounded-sm border-border bg-white"
                    disabled={running}
                />
                <p className="text-[11px] text-ciff-inkSoft leading-snug">
                    Anchoring searches to the identifier prevents confusion with
                    similarly named entities.
                </p>
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="dd-website-input" className="text-[11px] font-semibold text-ciff-ink uppercase tracking-wider">
                    Official Website <span className="text-ciff-inkSoft normal-case tracking-normal font-normal">(optional)</span>
                </Label>
                <Input
                    id="dd-website-input"
                    data-testid="dd-website-input"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://www.example.org"
                    className="font-mono text-sm rounded-sm border-border bg-white"
                    disabled={running}
                />
                <p className="text-[11px] text-ciff-inkSoft leading-snug">
                    Focuses searches on the org&apos;s official domain for a richer profile.
                </p>
            </div>

            <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold text-ciff-ink uppercase tracking-wider">
                    Report mode
                </Label>
                <div className="grid grid-cols-2 gap-2">
                    <button
                        type="button"
                        onClick={() => setMode("fast")}
                        disabled={running}
                        data-testid="dd-mode-fast"
                        className={`text-left px-3 py-2 border transition-colors duration-150 ${mode === "fast" ? "border-ciff-magenta bg-ciff-magenta/5" : "border-border bg-white hover:border-ciff-ink"}`}
                    >
                        <div className={`text-[11px] font-semibold ${mode === "fast" ? "text-ciff-magenta" : "text-ciff-ink"}`}>FAST</div>
                        <div className={`text-[10px] text-ciff-inkSoft font-mono mt-0.5`}>Haiku 4.5 · 2 hops · ~4 min</div>
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode("deep")}
                        disabled={running}
                        data-testid="dd-mode-deep"
                        className={`text-left px-3 py-2 border transition-colors duration-150 ${mode === "deep" ? "border-ciff-magenta bg-ciff-magenta/5" : "border-border bg-white hover:border-ciff-ink"}`}
                    >
                        <div className={`text-[11px] font-semibold ${mode === "deep" ? "text-ciff-magenta" : "text-ciff-ink"}`}>DEEP</div>
                        <div className={`text-[10px] text-ciff-inkSoft font-mono mt-0.5`}>Sonnet 4.5 · 3 hops · ~8 min</div>
                    </button>
                </div>
                <p className="text-[11px] text-ciff-inkSoft leading-snug">
                    Snowball mode: 3 hops in Deep chases leaders, affiliates and
                    funders identified in the first hop.
                </p>
            </div>


            <div className="flex items-center gap-2 pt-1">
                <Button
                    type="submit"
                    disabled={!canSubmit}
                    data-testid={DD.generateBtn}
                    className="bg-ciff-magenta hover:bg-ciff-magentaDeep text-white rounded-sm h-10 text-sm font-semibold flex-1 transition-colors duration-150"
                >
                    {running ? (
                        <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Generating…
                        </>
                    ) : (
                        <>
                            <Search className="h-4 w-4 mr-2" />
                            Generate Report
                        </>
                    )}
                </Button>
                {status !== "idle" && (
                    <Button
                        type="button"
                        onClick={onReset}
                        variant="outline"
                        data-testid={DD.resetBtn}
                        className="rounded-sm h-10 text-sm font-medium border-border text-ciff-ink hover:bg-ciff-paper2"
                        title="Reset"
                    >
                        <RotateCcw className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </form>
    );
}
